// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Guild} from '@app/features/guild/models/Guild';
import * as GuildTagCommands from '@app/features/guild_tag/commands/GuildTagCommands';
import GuildTags from '@app/features/guild_tag/state/GuildTags';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {useCallback, useState} from 'react';

export const SERVER_TAG_ROW_DESCRIPTOR = msg({
	message: 'Server Tag',
	comment: 'Community menu row that wears or takes off the tag of this community. Keep it concise.',
});
export const WEAR_FAILED_DESCRIPTOR = msg({
	message: 'Could not change your server tag. Try again.',
	comment: 'Error toast when wearing or taking off a server tag fails.',
});

// The menus open on guilds the user is in, so the tag comes off the guild and never asks the by-ID read. The worn state
// follows USER_UPDATE; call it inside an observer.
export function useGuildTagToggle(guild: Guild) {
	const {i18n} = useLingui();
	const [isSaving, setIsSaving] = useState(false);
	const tag = guild.guildTag && GuildTags.enabled ? {tag: guild.guildTag, badge: guild.guildTagBadge} : null;
	const worn = Users.getCurrentUser()?.tagGuildId === guild.id;
	const toggle = useCallback(async () => {
		if (isSaving) return;
		setIsSaving(true);
		try {
			await GuildTagCommands.wear(worn ? null : guild.id);
		} catch {
			ToastCommands.createToast({type: 'error', children: i18n._(WEAR_FAILED_DESCRIPTOR)});
		} finally {
			setIsSaving(false);
		}
	}, [guild.id, i18n, isSaving, worn]);
	return {tag, worn, isSaving, toggle};
}
